import React, { useState, useEffect, useRef, useMemo } from "react";
import { Calendar as CalendarIcon, ChevronDown, ChevronLeft, ChevronRight, X } from "lucide-react";

const PRIMARY = "#6C47FF";
const PRIMARY_TINT = "#eae9f4";
const IST = "Asia/Kolkata";
const MONTH_SHORT = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

// -------- IST helpers --------
function istParts(d) {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: IST, year: "numeric", month: "2-digit", day: "2-digit",
  }).formatToParts(d);
  const get = (t) => parts.find((p) => p.type === t).value;
  return { y: +get("year"), m: +get("month"), d: +get("day") };
}
function istDate(y, m, d) { // returns Date at IST midnight (as UTC instant)
  return new Date(`${y}-${String(m).padStart(2, "0")}-${String(d).padStart(2, "0")}T00:00:00+05:30`);
}
function istStartISO(y, m, d) { return istDate(y, m, d).toISOString(); }
function istEndISO(y, m, d) {
  return new Date(`${y}-${String(m).padStart(2, "0")}-${String(d).padStart(2, "0")}T23:59:59.999+05:30`).toISOString();
}
function addDays(y, m, d, n) {
  const dt = istDate(y, m, d);
  dt.setUTCDate(dt.getUTCDate() + n);
  return istParts(dt);
}
function daysInMonth(y, m) {
  return new Date(y, m, 0).getDate(); // m is 1-12
}
// Monday-based weekday index (0=Mon..6=Sun) for IST date (y,m,d)
function mondayIdx(y, m, d) {
  const dow = istDate(y, m, d).getUTCDay(); // 0=Sun..6=Sat but in UTC — since IST midnight is prev-day UTC 18:30, use a different approach
  // Simpler: build a native Date at local midnight and use its day, but timezone can shift. Trick: compute from y-m-d directly.
  // Zeller-like: JS Date treats yyyy-mm-dd as local. Use it.
  const j = new Date(y, m - 1, d); // local
  const jsDow = j.getDay(); // 0=Sun..6=Sat
  return (jsDow + 6) % 7; // 0=Mon..6=Sun
}
function isoToParts(iso) {
  if (!iso) return null;
  try { return istParts(new Date(iso)); } catch { return null; }
}
function partsEq(a, b) { return a && b && a.y === b.y && a.m === b.m && a.d === b.d; }
function partsLt(a, b) {
  if (a.y !== b.y) return a.y < b.y;
  if (a.m !== b.m) return a.m < b.m;
  return a.d < b.d;
}
function fmtLong({ y, m, d }) { return `${String(d).padStart(2, "0")} ${MONTH_SHORT[m - 1]} ${y}`; }
function fmtDMY({ y, m, d }) { return `${String(d).padStart(2, "0")}/${String(m).padStart(2, "0")}/${y}`; }
function parseDMY(s) {
  const m = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec((s || "").trim());
  if (!m) return null;
  const d = +m[1], mo = +m[2], y = +m[3];
  if (mo < 1 || mo > 12 || d < 1 || d > daysInMonth(y, mo)) return null;
  return { y, m: mo, d };
}
function today() { return istParts(new Date()); }

// -------- Presets --------
const PRESET_ALIAS = { month: "this_month", week: "this_week", all: "all_time" };
export const RANGE_PRESETS = [
  "today", "yesterday", "this_week", "last_7_days", "last_week", "last_14_days",
  "this_month", "last_30_days", "last_month", "all_time",
];
const PRESET_LABELS = {
  custom: "Custom",
  today: "Today",
  yesterday: "Yesterday",
  this_week: "This week (Mon – Today)",
  last_7_days: "Last 7 days",
  last_week: "Last week (Mon – Sun)",
  last_14_days: "Last 14 days",
  this_month: "This month",
  last_30_days: "Last 30 days",
  last_month: "Last month",
  all_time: "All time",
};
const MOBILE_PILLS = ["today", "yesterday", "last_7_days", "last_14_days", "this_month", "last_30_days", "last_month", "all_time"];

function presetParts(preset) {
  const t = today();
  const norm = PRESET_ALIAS[preset] || preset;
  switch (norm) {
    case "today": return { start: t, end: t };
    case "yesterday": { const y = addDays(t.y, t.m, t.d, -1); return { start: y, end: y }; }
    case "this_week": { const dow = mondayIdx(t.y, t.m, t.d); return { start: addDays(t.y, t.m, t.d, -dow), end: t }; }
    case "last_7_days": return { start: addDays(t.y, t.m, t.d, -6), end: t };
    case "last_14_days": return { start: addDays(t.y, t.m, t.d, -13), end: t };
    case "last_30_days": return { start: addDays(t.y, t.m, t.d, -29), end: t };
    case "this_month": return { start: { y: t.y, m: t.m, d: 1 }, end: t };
    case "last_month": {
      const first = { y: t.y, m: t.m, d: 1 };
      const lastPrev = addDays(first.y, first.m, first.d, -1);
      return { start: { y: lastPrev.y, m: lastPrev.m, d: 1 }, end: lastPrev };
    }
    case "last_week": {
      const dow = mondayIdx(t.y, t.m, t.d);
      const lastSun = addDays(t.y, t.m, t.d, -dow - 1);
      const lastMon = addDays(lastSun.y, lastSun.m, lastSun.d, -6);
      return { start: lastMon, end: lastSun };
    }
    case "all_time": return { start: null, end: null };
    default: return { start: null, end: null };
  }
}

export function computeRange(preset) {
  const p = presetParts(preset);
  return {
    from: p.start ? istStartISO(p.start.y, p.start.m, p.start.d) : null,
    to: p.end ? istEndISO(p.end.y, p.end.m, p.end.d) : null,
  };
}

function partsToRange(start, end) {
  return {
    from: start ? istStartISO(start.y, start.m, start.d) : null,
    to: end ? istEndISO(end.y, end.m, end.d) : null,
  };
}

// -------- Calendar grid --------
function monthCells(year, month) {
  const first = mondayIdx(year, month, 1);
  const dim = daysInMonth(year, month);
  const prevDim = month === 1 ? daysInMonth(year - 1, 12) : daysInMonth(year, month - 1);
  const cells = [];
  for (let i = 0; i < first; i++) cells.push({ y: month === 1 ? year - 1 : year, m: month === 1 ? 12 : month - 1, d: prevDim - first + 1 + i, other: true });
  for (let i = 1; i <= dim; i++) cells.push({ y: year, m: month, d: i, other: false });
  while (cells.length % 7 !== 0 || cells.length < 42) {
    const last = cells[cells.length - 1];
    const nxt = addDays(last.y, last.m, last.d, 1);
    cells.push({ ...nxt, other: nxt.m !== month });
    if (cells.length >= 42) break;
  }
  return cells;
}

function CellStyle({ cell, start, end, isToday }) {
  const inRange = start && end && !partsLt(cell, start) && !partsLt(end, cell);
  const isStart = start && partsEq(cell, start);
  const isEnd = end && partsEq(cell, end);
  const style = {};
  let cls = "flex items-center justify-center text-[13px] rounded-full min-h-[36px] min-w-[36px] cursor-pointer select-none";
  if (isStart || isEnd) {
    style.backgroundColor = PRIMARY;
    style.color = "#fff";
    cls += " font-[600]";
  } else if (inRange) {
    style.backgroundColor = PRIMARY_TINT;
    style.color = "#1A1A2E";
    style.borderRadius = "0";
  } else if (isToday) {
    style.border = `1px solid ${PRIMARY}`;
    style.color = PRIMARY;
    cls += " font-[600]";
  }
  if (cell.other) style.color = "#c3c1bd";
  return { cls, style };
}

function MonthView({ year, month, start, end, onPick, isMobile }) {
  const cells = monthCells(year, month);
  const t = today();
  return (
    <div>
      <div className="text-sm font-[600] mb-2 uppercase tracking-wider text-[#1A1A2E]">
        {MONTH_SHORT[month - 1]} {year}
      </div>
      <div className={`grid grid-cols-7 gap-y-1 ${isMobile ? "gap-x-0" : "gap-x-0"} text-[11px] text-[#6B7280] mb-1 font-[600]`}>
        {["M", "T", "W", "T", "F", "S", "S"].map((d, i) => <div key={i} className="text-center">{d}</div>)}
      </div>
      <div className="grid grid-cols-7 gap-y-1">
        {cells.map((c, i) => {
          const isToday = partsEq(c, t);
          const { cls, style } = CellStyle({ cell: c, start, end, isToday });
          return (
            <div key={i} className="flex items-center justify-center" style={{ minHeight: isMobile ? 44 : 36 }}>
              <div
                onClick={() => !c.other && onPick(c)}
                className={cls}
                style={{ ...style, minHeight: isMobile ? 44 : 36, minWidth: isMobile ? 44 : 36, pointerEvents: c.other ? "none" : "auto" }}
                data-testid={`cal-day-${c.y}-${c.m}-${c.d}`}
              >
                {c.d}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// -------- Main Component --------
export default function DateRangePicker({ value, onChange, defaultPreset, testid = "date-range" }) {
  const [open, setOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const wrapperRef = useRef(null);

  // Draft state (before Apply)
  const [start, setStart] = useState(null);
  const [end, setEnd] = useState(null);
  const [activePreset, setActivePreset] = useState(() => {
    // If a value is provided, try to match it against known presets so the label is correct
    if (value?.from || value?.to) {
      for (const p of RANGE_PRESETS) {
        const r = computeRange(p);
        if (r.from === value.from && r.to === value.to) return p;
      }
      return "custom";
    }
    return defaultPreset ? (PRESET_ALIAS[defaultPreset] || defaultPreset) : null;
  });
  const [pickingEnd, setPickingEnd] = useState(false);
  const [viewMonth, setViewMonth] = useState(() => { const t = today(); return { y: t.y, m: t.m }; });
  const [daysUpToToday, setDaysUpToToday] = useState(30);
  const [daysUpToYesterday, setDaysUpToYesterday] = useState(30);
  const [compare, setCompare] = useState(false);
  const [startText, setStartText] = useState("");
  const [endText, setEndText] = useState("");

  useEffect(() => {
    const mq = window.matchMedia("(max-width: 767px)");
    const update = () => setIsMobile(mq.matches);
    update();
    mq.addEventListener("change", update);
    return () => mq.removeEventListener("change", update);
  }, []);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    const onDoc = (e) => { if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setOpen(false); };
    if (open) {
      document.addEventListener("keydown", onKey);
      document.addEventListener("mousedown", onDoc);
    }
    return () => { document.removeEventListener("keydown", onKey); document.removeEventListener("mousedown", onDoc); };
  }, [open]);

  // Load current value into draft when opening
  useEffect(() => {
    if (open) {
      const s = isoToParts(value?.from);
      const e = isoToParts(value?.to);
      setStart(s);
      setEnd(e);
      setStartText(s ? fmtDMY(s) : "");
      setEndText(e ? fmtDMY(e) : "");
      setPickingEnd(false);
      if (s) setViewMonth({ y: s.y, m: s.m });
    }
  }, [open, value]);

  const applyPreset = (p) => {
    const { start: ps, end: pe } = presetParts(p);
    setStart(ps);
    setEnd(pe);
    setStartText(ps ? fmtDMY(ps) : "");
    setEndText(pe ? fmtDMY(pe) : "");
    setActivePreset(p);
    setPickingEnd(false);
    if (ps) setViewMonth({ y: ps.y, m: ps.m });
    // Auto-apply and close for any real preset. Custom stays open for calendar selection.
    if (p !== "custom") {
      onChange(partsToRange(ps, pe));
      setOpen(false);
    }
  };

  const pickDate = (cell) => {
    // Range selection flow:
    //  - No start yet, OR a full range already exists → start a fresh range from this click
    //  - Only start is set → this click completes the range as the end (or resets start if before it)
    if (!start || (start && end)) {
      setStart(cell);
      setEnd(null);
      setStartText(fmtDMY(cell));
      setEndText("");
      setPickingEnd(true);
    } else {
      // start is set, end is not
      if (partsLt(cell, start)) {
        // clicked earlier than start → treat as new start
        setStart(cell);
        setEnd(null);
        setStartText(fmtDMY(cell));
        setEndText("");
        setPickingEnd(true);
      } else {
        setEnd(cell);
        setEndText(fmtDMY(cell));
        setPickingEnd(false);
      }
    }
    setActivePreset("custom");
  };

  const applyDaysUpTo = (n, mode) => {
    const t = today();
    const endP = mode === "yesterday" ? addDays(t.y, t.m, t.d, -1) : t;
    const startP = addDays(endP.y, endP.m, endP.d, -(n - 1));
    setStart(startP);
    setEnd(endP);
    setStartText(fmtDMY(startP));
    setEndText(fmtDMY(endP));
    setActivePreset("custom");
    setViewMonth({ y: startP.y, m: startP.m });
  };

  const commitTextInput = (which, text) => {
    const p = parseDMY(text);
    if (!p) return;
    if (which === "start") { setStart(p); setViewMonth({ y: p.y, m: p.m }); }
    else setEnd(p);
    setActivePreset("custom");
  };

  const apply = () => {
    // Only commit if the user has selected either:
    //  - Both start and end (a complete range), or
    //  - Neither (clearing the filter)
    // A partial selection (start without end) is treated as incomplete and ignored.
    if (start && !end) return;
    onChange(partsToRange(start, end));
    setOpen(false);
  };

  const applyDisabled = !!(start && !end);

  const cancel = () => setOpen(false);

  const label = useMemo(() => {
    if (activePreset && activePreset !== "custom") return PRESET_LABELS[activePreset];
    const s = isoToParts(value?.from);
    const e = isoToParts(value?.to);
    if (!s && !e) return "All time";
    if (s && e) return `${fmtLong(s)} – ${fmtLong(e)}`;
    if (s) return `From ${fmtLong(s)}`;
    return `Until ${fmtLong(e)}`;
  }, [activePreset, value]);

  const nextMonthView = useMemo(() => {
    const nm = viewMonth.m === 12 ? 1 : viewMonth.m + 1;
    const ny = viewMonth.m === 12 ? viewMonth.y + 1 : viewMonth.y;
    return { y: ny, m: nm };
  }, [viewMonth]);

  const navMonth = (dir) => {
    setViewMonth((v) => {
      const m = v.m + dir;
      if (m < 1) return { y: v.y - 1, m: 12 };
      if (m > 12) return { y: v.y + 1, m: 1 };
      return { y: v.y, m };
    });
  };

  return (
    <div className="relative inline-block" ref={wrapperRef} data-testid={testid}>
      <button
        onClick={() => setOpen((v) => !v)}
        data-testid={`${testid}-trigger`}
        className="inline-flex items-center gap-2 px-3 py-2 rounded-[6px] border border-[#E8EAEF] bg-white text-sm font-[600] text-[#1A1A2E] min-h-[44px] hover:border-[#6C47FF]"
      >
        <CalendarIcon className="h-4 w-4 text-[#6B7280]" />
        <span>{label}</span>
        <ChevronDown className="h-4 w-4 text-[#6B7280]" />
      </button>

      {open && !isMobile && (
        <div className="absolute z-50 right-0 mt-2 bg-white border border-[#E8EAEF] rounded-[12px] shadow-[0_8px_24px_rgba(0,0,0,0.12)] w-[720px] max-w-[95vw] flex flex-col" style={{ maxHeight: "80vh" }}>
          <div className="flex flex-1 overflow-hidden">
            {/* Left panel */}
            <div className="w-[220px] border-r border-[#E8EAEF] overflow-y-auto py-2">
              {[
                "custom", "today", "yesterday", "this_week", "last_7_days",
                "last_week", "last_14_days", "this_month", "last_30_days", "last_month", "all_time",
              ].map((p) => (
                <button
                  key={p}
                  onClick={() => applyPreset(p)}
                  data-testid={`${testid}-preset-${p}`}
                  className={`w-full flex items-center justify-between px-4 py-2.5 text-sm font-[460] text-left hover:bg-[#F0F1F7] ${activePreset === p ? "bg-[#f1f0f7] font-[600]" : ""}`}
                >
                  <span>{PRESET_LABELS[p]}</span>
                  {(p === "this_week" || p === "last_week") && <ChevronRight className="h-3 w-3 text-[#6B7280]" />}
                </button>
              ))}
              <div className="border-t border-[#E8EAEF] mt-2 pt-3 px-4 pb-3 space-y-2">
                <div className="flex items-center gap-2 text-xs">
                  <input type="number" min="1" value={daysUpToToday} onChange={(e) => setDaysUpToToday(+e.target.value || 1)} onBlur={() => applyDaysUpTo(daysUpToToday, "today")} data-testid={`${testid}-days-today`} className="w-14 rounded-[4px] border border-[#E8EAEF] px-1.5 py-1 text-sm bg-white" style={{ fontSize: 16 }} />
                  <span className="text-[#1A1A2E]">days up to today</span>
                </div>
                <div className="flex items-center gap-2 text-xs">
                  <input type="number" min="1" value={daysUpToYesterday} onChange={(e) => setDaysUpToYesterday(+e.target.value || 1)} onBlur={() => applyDaysUpTo(daysUpToYesterday, "yesterday")} data-testid={`${testid}-days-yesterday`} className="w-14 rounded-[4px] border border-[#E8EAEF] px-1.5 py-1 text-sm bg-white" style={{ fontSize: 16 }} />
                  <span className="text-[#1A1A2E]">days up to yesterday</span>
                </div>
              </div>
              <div className="border-t border-[#E8EAEF] pt-3 px-4 flex items-center justify-between">
                <span className="text-sm font-[460]">Compare</span>
                <button onClick={() => setCompare((v) => !v)} data-testid={`${testid}-compare`} className={`relative w-10 h-5 rounded-full transition-colors ${compare ? "bg-[#6C47FF]" : "bg-[#E8EAEF]"}`}>
                  <span className={`absolute top-0.5 h-4 w-4 rounded-full bg-white transition-all ${compare ? "left-[22px]" : "left-0.5"}`} />
                </button>
              </div>
            </div>
            {/* Right panel */}
            <div className="flex-1 flex flex-col min-w-0">
              <div className="flex items-center justify-between gap-4 p-4 border-b border-[#E8EAEF]">
                <div className="flex-1">
                  <div className="text-[11px] text-[#6B7280] font-[600]">Start date</div>
                  <input value={startText} onChange={(e) => setStartText(e.target.value)} onBlur={() => commitTextInput("start", startText)} placeholder="DD/MM/YYYY" data-testid={`${testid}-start-input`} className="w-full border-b border-[#1A1A2E] py-1 text-sm bg-transparent focus:outline-none" style={{ fontSize: 16 }} />
                </div>
                <span className="text-[#6B7280] pt-4">—</span>
                <div className="flex-1">
                  <div className="text-[11px] text-[#6B7280] font-[600]">End date</div>
                  <input value={endText} onChange={(e) => setEndText(e.target.value)} onBlur={() => commitTextInput("end", endText)} placeholder="DD/MM/YYYY" data-testid={`${testid}-end-input`} className="w-full border-b border-[#1A1A2E] py-1 text-sm bg-transparent focus:outline-none" style={{ fontSize: 16 }} />
                </div>
                <div className="flex items-center gap-1">
                  <button onClick={() => navMonth(-1)} data-testid={`${testid}-prev-month`} className="p-1 rounded-full hover:bg-[#F0F1F7]"><ChevronLeft className="h-4 w-4" /></button>
                  <button onClick={() => navMonth(1)} data-testid={`${testid}-next-month`} className="p-1 rounded-full hover:bg-[#F0F1F7]"><ChevronRight className="h-4 w-4" /></button>
                </div>
              </div>
              <div className="flex-1 overflow-y-auto p-4 space-y-6">
                <MonthView year={viewMonth.y} month={viewMonth.m} start={start} end={end} onPick={pickDate} isMobile={false} />
                <MonthView year={nextMonthView.y} month={nextMonthView.m} start={start} end={end} onPick={pickDate} isMobile={false} />
              </div>
            </div>
          </div>
          <div className="flex items-center justify-end gap-2 border-t border-[#E8EAEF] px-4 py-3">
            <button onClick={cancel} data-testid={`${testid}-cancel`} className="px-3 py-1.5 text-sm font-[600] text-[#6C47FF]">Cancel</button>
            <button onClick={apply} disabled={applyDisabled} data-testid={`${testid}-apply`} className={`px-3 py-1.5 text-sm font-[600] ${applyDisabled ? "text-[#c3c1bd] cursor-not-allowed" : "text-[#6C47FF]"}`}>Apply</button>
          </div>
        </div>
      )}

      {open && isMobile && (
        <>
          <div className="fixed inset-0 z-40" style={{ backgroundColor: "rgba(0,0,0,0.5)" }} onClick={cancel} />
          <div className="fixed inset-x-0 bottom-0 z-50 bg-white rounded-t-[16px] flex flex-col" style={{ maxHeight: "90vh", transform: "translateY(0)", transition: "transform 0.3s ease" }}>
            <div className="flex justify-center pt-2 pb-1">
              <div className="h-1.5 w-10 rounded-full bg-[#E8EAEF]" />
            </div>
            <div className="px-4 pb-3 grid grid-cols-2 gap-3">
              <div>
                <div className="text-[11px] text-[#6B7280] font-[600]">Start date</div>
                <input value={startText} onChange={(e) => setStartText(e.target.value)} onBlur={() => commitTextInput("start", startText)} placeholder="DD/MM/YYYY" data-testid={`${testid}-start-input`} className="w-full border border-[#E8EAEF] rounded-[6px] px-2 py-2 text-sm mt-1" style={{ fontSize: 16 }} />
              </div>
              <div>
                <div className="text-[11px] text-[#6B7280] font-[600]">End date</div>
                <input value={endText} onChange={(e) => setEndText(e.target.value)} onBlur={() => commitTextInput("end", endText)} placeholder="DD/MM/YYYY" data-testid={`${testid}-end-input`} className="w-full border border-[#E8EAEF] rounded-[6px] px-2 py-2 text-sm mt-1" style={{ fontSize: 16 }} />
              </div>
            </div>
            <div className="px-4 pb-3 overflow-x-auto whitespace-nowrap flex gap-2" style={{ scrollbarWidth: "none" }}>
              {MOBILE_PILLS.map((p) => (
                <button
                  key={p}
                  onClick={() => applyPreset(p)}
                  data-testid={`${testid}-pill-${p}`}
                  className="inline-flex items-center rounded-full border font-[600] text-xs"
                  style={{
                    padding: "8px 16px",
                    borderColor: activePreset === p ? PRIMARY : "#e8e4f0",
                    background: activePreset === p ? PRIMARY : "#fff",
                    color: activePreset === p ? "#fff" : "#1A1A2E",
                  }}
                >
                  {PRESET_LABELS[p]}
                </button>
              ))}
            </div>
            <div className="px-4 flex items-center justify-between mb-2">
              <button onClick={() => navMonth(-1)} data-testid={`${testid}-prev-month`} className="p-2 rounded-full hover:bg-[#F0F1F7]"><ChevronLeft className="h-5 w-5" /></button>
              <div className="text-sm font-[600]">{MONTH_SHORT[viewMonth.m - 1]} {viewMonth.y}</div>
              <button onClick={() => navMonth(1)} data-testid={`${testid}-next-month`} className="p-2 rounded-full hover:bg-[#F0F1F7]"><ChevronRight className="h-5 w-5" /></button>
            </div>
            <div className="flex-1 overflow-y-auto px-4 pb-3">
              <MonthView year={viewMonth.y} month={viewMonth.m} start={start} end={end} onPick={pickDate} isMobile />
            </div>
            <div className="grid grid-cols-2 gap-3 p-4 border-t border-[#E8EAEF]">
              <button onClick={cancel} data-testid={`${testid}-cancel`} className="rounded-[8px] border border-[#6C47FF] text-[#6C47FF] text-sm font-[600]" style={{ minHeight: 48 }}>Cancel</button>
              <button onClick={apply} disabled={applyDisabled} data-testid={`${testid}-apply`} className={`rounded-[8px] text-sm font-[600] ${applyDisabled ? "bg-[#c3c1bd] text-white cursor-not-allowed" : "btn-mx"}`} style={{ minHeight: 48 }}>Apply</button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
